import type {
  RorosDecisionNoteDTO,
  DecisionMethodProfile,
  CreateDecisionPayload,
  UpdateDecisionPayload,
} from '@/types/roros/decisions';
import { fetchJson, mutate, qs } from './base-roros.service';

// ─── Decisions ──────────────────────────────────────────────────────────────

export const RorosDecisionService = {
  list: (params?: Record<string, string>) =>
    fetchJson<RorosDecisionNoteDTO[]>(`/api/v1/decisions${qs(params)}`),
  create: (payload: CreateDecisionPayload) =>
    mutate<RorosDecisionNoteDTO>('/api/v1/decisions', 'POST', payload),
  get: (id: string) =>
    fetchJson<RorosDecisionNoteDTO>(`/api/v1/decisions/${id}`),
  update: (id: string, payload: UpdateDecisionPayload) =>
    mutate<RorosDecisionNoteDTO>(`/api/v1/decisions/${id}`, 'PUT', payload),
  submit: (id: string) =>
    mutate<RorosDecisionNoteDTO>(`/api/v1/decisions/${id}/submit`, 'POST'),
  approve: (id: string, payload?: { reason?: string }) =>
    mutate<RorosDecisionNoteDTO>(`/api/v1/decisions/${id}/approve`, 'POST', payload),
  reject: (id: string, payload?: { reason?: string }) =>
    mutate<RorosDecisionNoteDTO>(`/api/v1/decisions/${id}/reject`, 'POST', payload),
  defer: (id: string) =>
    mutate<RorosDecisionNoteDTO>(`/api/v1/decisions/${id}/defer`, 'POST'),
  archive: (id: string) =>
    mutate<RorosDecisionNoteDTO>(`/api/v1/decisions/${id}/archive`, 'POST'),
  advisoryConsult: (id: string, payload: { persona_ids: string[]; question: string }) =>
    mutate<RorosDecisionNoteDTO>(`/api/v1/decisions/${id}/advisory-consult`, 'POST', payload),
  /** A.1 EO Connect Demo Phase 2 — Run full Decision Moderator (classify + recommend + advisory). */
  runModerator: (id: string, payload?: { context?: string }) =>
    mutate<{
      impact: string;
      methodSlug: string;
      methodLabel: string;
      rationale: string;
      advisoryConsultation: Record<string, unknown> | null;
    }>(`/api/v1/decisions/${id}/run-moderator`, 'POST', payload ?? {}),
  /**
   * Sven dogfood 2026-05-03 — preview the moderator's recommendation
   * against draft fields, BEFORE the decision is persisted. No DB
   * write; no advisory consultation. Used by DecisionFormDialog in
   * CREATE mode so the founder sees the moderator's classification +
   * method suggestion while still drafting.
   */
  previewModerator: (payload: { title: string; description?: string | null; domainId?: string | null }) =>  // A1
    mutate<{
      impact: string;
      methodSlug: string;
      methodLabel: string;
      rationale: string;
      /**
       * Sven dogfood 2026-05-03: optional polished decision statement the
       * dialog uses to pre-fill the Decision textarea. Optional because a
       * legacy moderator deployment may omit the field — clients keep the
       * user's typed text in that case.
       */
      decisionStatement?: string;
    }>(`/api/v1/decisions/moderate-preview`, 'POST', payload),
  getMethods: () =>
    fetchJson<DecisionMethodProfile[]>('/api/v1/decisions/methods'),
  getLatency: () =>
    fetchJson<Record<string, unknown>>('/api/v1/decisions/latency'),
};
